import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {
  Company,
  CompanyAlliance,
  CompanyPersonalization,
  CompanyStats,
  CompanyType,
  QuoteTemplateStyle,
} from '@models/company.model';
import { CompanyBankAccountType } from '@models/shared.model';
import { Paginable, PaginableParams } from '@utils/paginable.utils';
import { changeDataIsoStringToDate } from '@utils/parse.utils';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';

interface CreateCompanyRequest {
  address: string;
  alliance?: CompanyAlliance | null;
  legalName: string;
  name: string;
  personalization?: CompanyPersonalization;
  quoteTemplateStyle?: QuoteTemplateStyle;
  taxId: string;
  type: CompanyType;
}

interface GetAllCompaniesParams extends PaginableParams {
  term?: string;
}

interface UpdateCompanyRequest {
  address?: string;
  bankAccountHolderName?: string | null;
  bankAccountHolderNationalId?: string | null;
  bankAccountNumber?: string | null;
  bankAccountType?: CompanyBankAccountType | null;
  bankEmail?: string | null;
  bankName?: string | null;
  description?: string;
  facebook?: string | null;
  instagram?: string | null;
  legalName?: string;
  logoUuid?: string | null;
  name?: string;
  quoteTemplateStyle?: QuoteTemplateStyle;
  type?: CompanyType;
  website?: string | null;
}

interface GetPublicUploadUrlResponse {
  publicUrl: string;
  signedUrl: string;
  uuid: string;
}

interface GetPrivateUploadUrlResponse {
  fileName: string;
  url: string;
}

interface GetUploadUrlResponse {
  url: string;
  uuid: string;
}

@Injectable({
  providedIn: 'root',
})
export class CompaniesService {
  constructor(
    private authService: AuthService,
    private http: HttpClient,
  ) {}

  async convertToPdf(file: File): Promise<File> {
    const formData = new FormData();
    formData.append('files', file, file.name);
    const blob = await firstValueFrom(this.http.post('/api/convert', formData, {
      responseType: 'blob',
    }));
    const pdfName = file.name.replace(/\.[^.]+$/, '.pdf');
    return new File([blob], pdfName, { type: 'application/pdf' });
  }

  async create(request: CreateCompanyRequest): Promise<Company> {
    const { data } = await firstValueFrom(this.http.post<{ data: Company; }>('/api/companies', request));
    return changeDataIsoStringToDate(data);
  }

  async get(id: number): Promise<Company> {
    const { data } = await firstValueFrom(this.http.get<{ data: Company; }>(`/api/companies/${id}`));
    return changeDataIsoStringToDate(data);
  }

  async getAll(params: GetAllCompaniesParams): Promise<Paginable<Company>> {
    const { limit, page, term } = params;
    const queryParams = new URLSearchParams({
      limit: `${limit}`,
      page: `${page}`,
      ...(term && { term }),
    });
    const { data } = await firstValueFrom(this.http.get<{ data: Paginable<Company>; }>(`/api/companies?${queryParams.toString()}`));
    data.data = data.data.map(changeDataIsoStringToDate);
    return data;
  }

  async getPublicUploadUrl(companyId: number): Promise<GetPublicUploadUrlResponse> {
    const { data } = await firstValueFrom(this.http.get<{ data: GetPublicUploadUrlResponse; }>(`/api/companies/${companyId}/public-upload-url`));
    return data;
  }

  async getStats(companyId: number): Promise<CompanyStats> {
    const { data } = await firstValueFrom(this.http.get<{ data: CompanyStats; }>(`/api/companies/${companyId}/stats`));
    return data;
  }

  async getPrivateUploadUrl(companyId: number, contentType: string): Promise<GetPrivateUploadUrlResponse> {
    const { data } = await firstValueFrom(this.http.post<{ data: GetPrivateUploadUrlResponse; }>(`/api/companies/${companyId}/private-upload-url`, {
      contentType,
    }, {
      params: {
        contentType,
      },
    }));
    return data;
  }

  async getUploadLogoUrl(companyId: number): Promise<GetUploadUrlResponse> {
    const { data } = await firstValueFrom(this.http.get<{ data: GetUploadUrlResponse; }>(`/api/companies/${companyId}/upload-logo-url`));
    return data;
  }

  async join(companyId: number): Promise<void> {
    await firstValueFrom(this.http.post<void>('/api/xsrfs', null));
    const { data: { customToken } } = await firstValueFrom(this.http.post<{ data: { customToken: string; }; }>(`/api/companies/${companyId}/join`, null));
    await this.authService.signIn(customToken);
  }

  async update(companyId: number, request: UpdateCompanyRequest): Promise<Company> {
    const { data } = await firstValueFrom(this.http.patch<{ data: Company; }>(`/api/companies/${companyId}`, request));
    return changeDataIsoStringToDate(data);
  }

  async uploadLogo(url: string, blob: Blob): Promise<void> {
    return firstValueFrom(this.http.put<void>(url, blob, {
      headers: {
        'Content-Type': 'image/jpeg',
        ...(environment.local && { 'Authorization': 'Firebase owner' }),
      },
    }));
  }

  async uploadFile(url: string, file: File): Promise<void> {
    return firstValueFrom(this.http.put<void>(url, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...(environment.local && { 'Authorization': 'Firebase owner' }),
      },
    }));
  }

  async uploadPublicImage(url: string, blob: Blob): Promise<void> {
    return firstValueFrom(this.http.put<void>(url, blob, {
      headers: {
        'Content-Type': 'image/jpeg',
        ...(environment.local && { 'Authorization': 'Firebase owner' }),
      },
    }));
  }
}
